/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/* eslint-disable @typescript-eslint/consistent-return */
import type { DropdownOption } from '@/components/common/ui/inputs/inputTypes'
import type {
  PhoneAI,
  TWOrganizationAIResponse,
  WeblinkAI,
} from '@/types/external/2work/doctypes/generatorOutput/ERP2Work/TWOrganizationAIResponse'

type AIAddressList = TWOrganizationAIResponse['addresses']
type AIPhoneList = TWOrganizationAIResponse['phone']
type AIWeblinkList = TWOrganizationAIResponse['weblinks']

interface ComparableAddress {
  address_line1?: string | undefined
  city?: string | undefined
  country?: string | undefined
  pincode?: string | undefined
}

interface PhoneTypeReference {
  name: string
  tw_phone_type_name: string
}

const DEFAULT_AI_ADDRESS_TITLE = 'Hauptadresse'
const DEFAULT_AI_PHONE_TYPE_NAME = 'Landline business'

const normalizeAiText = (value: string): string => {
  return value
    .trim()
    .replaceAll(/[^0-9\p{L}]+/gu, ' ')
    .replaceAll(/\s+/gu, ' ')
    .toLocaleLowerCase()
}

const getStringValue = (value: unknown): string | undefined => {
  return typeof value === 'string' ? value.trim() : undefined
}

const normalizeComparableValue = (value: string | undefined): string => {
  return value?.trim().toLocaleLowerCase() ?? ''
}

const normalizeComparableUrl = (value: string | undefined): string => {
  return normalizeComparableValue(value)
    .replace(/^https?:\/\//u, '')
    .replace(/^www\./u, '')
    .replace(/\/+$/u, '')
}

const normalizeComparablePhone = (value: string | undefined): string => {
  return normalizeComparableValue(value).replaceAll(/\D/gu, '')
}

const getComparableAddressKey = (address: ComparableAddress): string => {
  return [address.address_line1, address.pincode, address.city, address.country]
    .map(normalizeComparableValue)
    .join('|')
}

const hasMatchingAiEmail = (existingEmails: (string | undefined)[], email: string): boolean => {
  const normalizedEmail = normalizeComparableValue(email)
  return existingEmails.some(
    (existingEmail) => normalizeComparableValue(existingEmail) === normalizedEmail,
  )
}

const hasMatchingAiPhone = (existingPhones: (string | undefined)[], phone: string): boolean => {
  const normalizedPhone = normalizeComparablePhone(phone)
  return existingPhones.some(
    (existingPhone) => normalizeComparablePhone(existingPhone) === normalizedPhone,
  )
}

const hasMatchingAiWeblink = (existingUrls: (string | undefined)[], url: string): boolean => {
  const normalizedUrl = normalizeComparableUrl(url)
  return existingUrls.some((existingUrl) => normalizeComparableUrl(existingUrl) === normalizedUrl)
}

const hasMatchingAiAddress = (
  existingAddresses: ComparableAddress[],
  address: ComparableAddress,
): boolean => {
  const addressKey = getComparableAddressKey(address)
  return existingAddresses.some(
    (existingAddress) => getComparableAddressKey(existingAddress) === addressKey,
  )
}

const compact = <T>(values: (T | undefined)[]): T[] => {
  return values.filter((value): value is T => value !== undefined)
}

const normalizeAiWeblinks = (weblinks: AIWeblinkList | null | undefined): WeblinkAI[] => {
  if (!weblinks) return []

  if (Array.isArray(weblinks)) {
    return compact(
      weblinks.map((entry) => {
        const type = getStringValue(entry.type) ?? null
        const url = getStringValue(entry.url)
        if (!url) return undefined

        return { type, url }
      }),
    )
  }

  return compact(
    Object.entries(weblinks).map(([type, url]) => {
      const normalizedType = getStringValue(type) ?? null
      const normalizedUrl = getStringValue(url)
      if (!normalizedType || !normalizedUrl) return undefined

      return { type: normalizedType, url: normalizedUrl }
    }),
  )
}

const normalizeAiPhones = (phones: AIPhoneList | null | undefined): PhoneAI[] => {
  if (!phones) return []

  if (Array.isArray(phones)) {
    return compact(
      phones.map((entry) => {
        const type = getStringValue(entry.type) ?? null
        const phone = getStringValue(entry.phone)
        if (!phone) return undefined

        return { phone, type }
      }),
    )
  }

  return compact(
    Object.entries(phones).map(([type, phone]) => {
      const normalizedType = getStringValue(type) ?? null
      const normalizedPhone = getStringValue(phone)
      if (!normalizedType || !normalizedPhone) return undefined

      return { phone: normalizedPhone, type: normalizedType }
    }),
  )
}

const tokenizeAiText = (value: string): string[] => {
  return normalizeAiText(value).split(' ').filter(Boolean)
}

const getAcronym = (value: string): string => {
  return tokenizeAiText(value)
    .map((token) => token[0] ?? '')
    .join('')
}

const getSimilarityScore = (left: string, right: string): number => {
  const leftTokens = tokenizeAiText(left)
  const rightTokens = tokenizeAiText(right)
  const rightTokenSet = new Set(rightTokens)
  const intersectionSize = leftTokens.filter((token) => rightTokenSet.has(token)).length
  const unionSize = new Set([...leftTokens, ...rightTokens]).size

  if (unionSize === 0) return 0

  return intersectionSize / unionSize
}

const findOptionValueFromAiText = (
  aiText: null | string | undefined,
  options: DropdownOption<string>[],
): string | undefined => {
  const rawText = getStringValue(aiText)
  if (!rawText) return undefined

  const exactValueMatch = options.find((option) => option.optionValue === rawText)
  if (exactValueMatch) return exactValueMatch.optionValue

  const exactLabelMatch = options.find((option) => option.optionLabel === rawText)
  if (exactLabelMatch) return exactLabelMatch.optionValue

  const normalizedText = normalizeAiText(rawText)
  const aiAcronym = getAcronym(rawText)
  const normalizedMatch = options.find((option) => {
    const normalizedOptionValue = normalizeAiText(option.optionValue)
    const normalizedOptionLabel = normalizeAiText(option.optionLabel)

    return (
      normalizedOptionValue === normalizedText ||
      normalizedOptionLabel === normalizedText ||
      normalizedOptionValue.includes(normalizedText) ||
      normalizedOptionLabel.includes(normalizedText) ||
      normalizedText.includes(normalizedOptionValue) ||
      normalizedText.includes(normalizedOptionLabel) ||
      (aiAcronym !== '' &&
        (getAcronym(option.optionValue) === aiAcronym ||
          getAcronym(option.optionLabel) === aiAcronym))
    )
  })

  if (normalizedMatch) return normalizedMatch.optionValue

  // eslint-disable-next-line @typescript-eslint/init-declarations
  let bestMatch: DropdownOption<string> | undefined
  let bestScore = 0

  options.forEach((option) => {
    const score = Math.max(
      getSimilarityScore(rawText, option.optionValue),
      getSimilarityScore(rawText, option.optionLabel),
    )

    if (score > bestScore) {
      bestMatch = option
      bestScore = score
    }
  })

  return bestScore >= 0.5 ? bestMatch?.optionValue : undefined
}

const resolveAiAddressTitle = (aiTitle: null | string | undefined): string => {
  const normalizedTitle = getStringValue(aiTitle)
  if (!normalizedTitle) return DEFAULT_AI_ADDRESS_TITLE

  return normalizedTitle
}

const resolveAiPhoneTypeValue = (
  aiType: null | string | undefined,
  options: DropdownOption<string>[],
  phoneTypes: PhoneTypeReference[],
): string | undefined => {
  return (
    findOptionValueFromAiText(aiType, options) ??
    phoneTypes.find((phoneType) => phoneType.tw_phone_type_name === DEFAULT_AI_PHONE_TYPE_NAME)
      ?.name
  )
}

const hasAnyAiData = (aiData: null | TWOrganizationAIResponse): boolean => {
  if (!aiData || Object.keys(aiData).length === 0) return false

  return !!(
    aiData.logo_url ||
    aiData.commercial_register ||
    aiData.tax_id ||
    aiData.vat_id ||
    aiData.description ||
    aiData.industry_type ||
    normalizeAiWeblinks(aiData.weblinks).length > 0 ||
    (aiData.email && aiData.email.length > 0) ||
    normalizeAiPhones(aiData.phone).length > 0 ||
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    ((aiData.addresses as AIAddressList | undefined)?.length ?? 0) > 0
  )
}

export {
  findOptionValueFromAiText,
  hasAnyAiData,
  hasMatchingAiAddress,
  hasMatchingAiEmail,
  hasMatchingAiPhone,
  hasMatchingAiWeblink,
  normalizeAiPhones,
  normalizeAiWeblinks,
  resolveAiAddressTitle,
  resolveAiPhoneTypeValue,
}
